import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MessageThread from "../../../../../components/chat/messageThread/MessageThread";
import {
  fetchTicketMessages,
  markTicketReadByAdmin,
} from "../../../../../features/supportTickets/supportTicketsSlice";
import TicketMessage from "./ticketMessage/TicketMessage";

const EMPTY = [];

const sideOf = (message) => (message.senderRole === "user" ? "player" : "support");

// One ticket's messages (keyed by ticketId by its parent, so each ticket
// starts fresh): shows what's cached at once and refreshes the newest page;
// marks the player's messages read once they're on screen.
const TicketThread = ({ ticketId }) => {
  const dispatch = useDispatch();
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const thread = useSelector((state) => state.supportTickets.threads[ticketId]);
  const unread = useSelector((state) => {
    const { tickets, activeTicket } = state.supportTickets;
    const ticket =
      tickets.find((entry) => entry._id === ticketId) ||
      (activeTicket?._id === ticketId ? activeTicket : null);
    return ticket?.unreadByAdmin || 0;
  });

  const loadNewest = useCallback(
    () => dispatch(fetchTicketMessages({ ticketId })),
    [dispatch, ticketId],
  );
  const loadOlder = useCallback(
    (oldest) => dispatch(fetchTicketMessages({ ticketId, beforeSeq: oldest.seq })),
    [dispatch, ticketId],
  );
  const markSeen = useCallback(
    () => dispatch(markTicketReadByAdmin(ticketId)),
    [dispatch, ticketId],
  );

  useEffect(() => {
    loadNewest();
  }, [loadNewest]);

  const renderMessage = useCallback(
    ({ message, side, groupStart, groupEnd }) => (
      <TicketMessage
        message={message}
        ticketId={ticketId}
        side={side}
        groupStart={groupStart}
        groupEnd={groupEnd}
        timeZone={timeZone}
      />
    ),
    [ticketId, timeZone],
  );

  return (
    <MessageThread
      items={thread?.items || EMPTY}
      status={thread?.status || "idle"}
      olderStatus={thread?.olderStatus || "idle"}
      hasMore={Boolean(thread?.hasMore)}
      unread={unread}
      timeZone={timeZone}
      sideOf={sideOf}
      renderMessage={renderMessage}
      onLoadNewest={loadNewest}
      onLoadOlder={loadOlder}
      onSeen={markSeen}
    />
  );
};

export default TicketThread;
